import { useRef, useState } from "react";
import { Button, Form } from "antd";
import { useController } from "react-hook-form";
import { Image, message, Tooltip } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { v4 as uuid } from "uuid";
import * as API from "../../../../services/apis";

function itemLayout(fullLabelWidth) {
  let layout = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 8 },
      lg: { span: 10 },
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 16 },
      lg: { span: 14 },
    },
  };

  if (fullLabelWidth === true) {
    layout.labelCol.lg.span = 24;
    layout.wrapperCol.lg.span = 24;
  }

  return layout;
}

export const ImageUpload = ({
  name,
  control,
  maxCount = 1,
  acceptType = ["image/*"],
  editable = true,
  label,
  fullLabelWidth,
  required,
  disabled
}) => {
  const { field, fieldState } = useController({ name, control });
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);

  // Controlled value handling
  const value = field.value || [];

const validateFile = (file) => {
  const isAcceptedType = acceptType.some((type) => {
    if (type.endsWith("/*")) {
      // Match broad type like image/*, video/* etc.
      const baseType = type.split("/")[0];
      return file.type.startsWith(baseType + "/");
    }
    // Exact MIME type match
    return file.type === type;
  });

  if (!isAcceptedType) {
    message.error(`File type ${file.type} is not supported!`);
    return false;
  }

  if (value.length >= maxCount) {
    message.error(`Oops! You’re only allowed to upload ${maxCount} images.`);
    return false;
  }

  return true;
};

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files);

    if (files.length > maxCount) {
      message.error(`Oops! You’re only allowed to upload ${maxCount} images.`);
      return;
    }

    // Validate files
    const validFiles = files.filter(validateFile);
    if (validFiles.length === 0) return;

    setUploading(true);
    const uploadedImages = [];

    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];

      try {
        // 1. Get the pre-signed URL
        const fileUrlResponse = await API.getFileUrl({
          blobName: `${file.name}_${uuid()}`,
          source: {
            name: process.env.REACT_APP_ENVIRONMENT,
          },
        });

        const presignedUrl = fileUrlResponse.data?.data?.document;
        if (!presignedUrl) {
          throw new Error("Presigned URL is missing in response");
        }

        await API.uploadFile({
          data: file,
          url: presignedUrl,
        });

        const imageObject = {
          name: file.name,
          url: presignedUrl,
        };

        uploadedImages.push(imageObject);
        message.success(`${file.name} uploaded successfully`);
      } catch (err) {
        console.error("Upload failed:", err);
        message.error(`Upload failed for ${file.name}: ${err.message}`);
      }
    }

    const newValue = [...value, ...uploadedImages];
    field.onChange(newValue);

    // Reset the input
    if (inputRef.current) {
      inputRef.current.value = "";
    }

    setUploading(false);
  };

  const handleRemove = (imageToRemove) => {
    const updated = value.filter((img) => img.url !== imageToRemove.url);
    field.onChange(updated);
    message.success("Image removed successfully");
  };

  return (
    <div style={{ width: "100%" }}>
      {editable && (
        <Form.Item
          label={label}
          {...itemLayout(fullLabelWidth)}
          required={required}
          labelAlign="left"
          validateStatus={fieldState.error ? "error" : ""}
          help={fieldState.error?.message}
        >
          <div >
            <input
              type="file"
              multiple
              accept={acceptType}
              onChange={handleFileChange}
              ref={inputRef}
              style={{ display: "none" }}
              disabled={uploading || disabled}
            />
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <Button
                icon={uploading ? <LoadingOutlined /> : <PlusOutlined />}
                onClick={() => inputRef.current?.click()}
                disabled={uploading || disabled}
                loading={uploading}
              >
                {uploading ? "Uploading..." : "Upload Images"}
              </Button>
            </div>
          </div>
        </Form.Item>
      )}

      {/* Image Grid */}
      {value.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
            gap: 12,
            marginBottom: 16,
          }}
        >
          {value.map((img, idx) => (
            <div
              key={img.id || idx}
              style={{
                position: "relative",
                border: "1px solid #d9d9d9",
                borderRadius: 8,
                overflow: "hidden",
                backgroundColor: "#fafafa",
              }}
            >
              <Image
                src={img.url}
                alt={img.name || `uploaded-${idx}`}
                width="100%"
                height={100}
                style={{ objectFit: "cover" }}
                preview={{
                  mask: (
                    <div style={{ textAlign: "center" }}>
                      <div>Preview</div>
                      {img.name && (
                        <div style={{ fontSize: "10px", marginTop: 4 }}>
                         {img.name.length > 20 ? `${img.name.slice(0, 20)}...` : img.name}
                        </div>
                      )}
                    </div>
                  ),
                }}
              />
              {!disabled && (
                <Tooltip title="Remove image">
                  <Button
                    type="primary"
                    shape="circle"
                    danger
                    icon={<DeleteOutlined />}
                    size="small"
                    onClick={() => handleRemove(img)}
                    disabled={uploading}
                    style={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    }}
                  />
                </Tooltip>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Error display */}
      {/* {fieldState.error && (
        <div
          style={{
            color: "#ff4d4f",
            marginTop: 8,
            fontSize: "14px",
            padding: "8px 12px",
            backgroundColor: "#fff2f0",
            border: "1px solid #ffccc7",
            borderRadius: 4,
          }}
        >
          {fieldState.error.message}
        </div>
      )} */}
    </div>
  );
};
